// components/CadCliente.tsx
import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { CadClienteRequest, ClienteResponse } from '@types';
import { SessionUtil } from '@utils';
import clienteController from '@controllers/clienteController';

const CadCliente: React.FC = () => {
  const navigate = useNavigate();
  const vendedor = SessionUtil.getVendedor();
  const vendCodigo = vendedor?.vendCodigo;

  const [loading, setLoading] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [responseData, setResponseData] = useState<ClienteResponse | null>(null);

  const [formData, setFormData] = useState<CadClienteRequest>({
    razao: '',
    nome: '',
    email: '',
    cnpj: '',
    endereco: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: '',
    cep: '',
    fone: '',
    vendedor: vendCodigo || ''
  });

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    
    // Formatação automática do CNPJ
    if (name === 'cnpj') {
      const cnpjLimpo = value.replace(/[^\d]/g, '');
      let cnpjFormatado = cnpjLimpo;
      
      if (cnpjLimpo.length <= 14) {
        cnpjFormatado = cnpjLimpo
          .replace(/^(\d{2})(\d)/, '$1.$2')
          .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
          .replace(/\.(\d{3})(\d)/, '.$1/$2')
          .replace(/(\d{4})(\d)/, '$1-$2');
      }
      
      setFormData((prev: CadClienteRequest) => ({
        ...prev,
        [name]: cnpjFormatado
      }));
      return;
    }

    setFormData((prev: CadClienteRequest) => ({
      ...prev,
      [name]: value
    }));
  };

  const validarFormulario = (): boolean => {
    if (!formData.nome.trim()) {
      setErrorMessage('O nome do cliente é obrigatório');
      setErrorDialogOpen(true);
      return false;
    }

    if (!formData.razao.trim()) {
      setErrorMessage('A razão social é obrigatória');
      setErrorDialogOpen(true);
      return false;
    }

    if (!formData.cnpj.trim()) {
      setErrorMessage('O CNPJ é obrigatório');
      setErrorDialogOpen(true);
      return false;
    }

    if (!clienteController.validarCNPJ(formData.cnpj)) {
      setErrorMessage('CNPJ inválido. Verifique o número digitado.');
      setErrorDialogOpen(true);
      return false;
    }

    if (!formData.cidade.trim()) {
      setErrorMessage('A cidade é obrigatória');
      setErrorDialogOpen(true);
      return false;
    }

    if (formData.email && !formData.email.includes('@')) {
      setErrorMessage('E-mail inválido');
      setErrorDialogOpen(true);
      return false;
    }

    return true;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setErrorMessage('');

    if (!validarFormulario()) {
      return;
    }

    try {
      setLoading(true);

      const response = await clienteController.cadastrarCliente(formData);

      if (response.retorno === 'ok') {
        setResponseData(response);
        setSuccessDialogOpen(true);
      } else {
        setErrorMessage(response.mensagem || 'Erro ao cadastrar cliente');
        setErrorDialogOpen(true);
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Erro ao cadastrar cliente. Tente novamente.');
      setErrorDialogOpen(true);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleVoltar = () => {
    navigate('/clientes');
  };

  const handleFecharSucesso = () => {
    setSuccessDialogOpen(false);
    navigate('/clientes');
  };

  const handleFecharErro = () => {
    setErrorDialogOpen(false);
  };

  return (
    <Box sx={{ pb: 10 }}>
      {/* Header */}
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton onClick={handleVoltar} disabled={loading}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6">Cadastrar Cliente</Typography>
      </Box>

      {/* Formulário */}
      <Box sx={{ px: 2 }}>
        <form onSubmit={handleSubmit}>
          {/* Dados Principais */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <PersonAddIcon color="primary" />
                <Typography variant="subtitle1" color="primary" fontWeight="bold">
                  DADOS PRINCIPAIS
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    label="Nome / Nome Fantasia"
                    name="nome"
                    value={formData.nome}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    label="Razão Social"
                    name="razao"
                    value={formData.razao}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    label="CNPJ"
                    name="cnpj"
                    value={formData.cnpj}
                    onChange={handleInputChange}
                    disabled={loading}
                    placeholder="00.000.000/0000-00"
                    inputProps={{ maxLength: 18 }}
                    helperText="Digite apenas números, a formatação é automática"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="E-mail"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Telefone"
                    name="fone"
                    value={formData.fone}
                    onChange={handleInputChange}
                    disabled={loading}
                    placeholder="(00) 00000-0000"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Endereço */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" color="primary" fontWeight="bold" mb={2}>
                ENDEREÇO
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <TextField
                    fullWidth
                    label="CEP"
                    name="cep"
                    value={formData.cep}
                    onChange={handleInputChange}
                    disabled={loading}
                    placeholder="00000-000"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Endereço"
                    name="endereco"
                    value={formData.endereco}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                </Grid>

                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Número"
                    name="numero"
                    value={formData.numero}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                </Grid>

                <Grid item xs={8}>
                  <TextField
                    fullWidth
                    label="Complemento"
                    name="complemento"
                    value={formData.complemento}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Bairro"
                    name="bairro"
                    value={formData.bairro}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                </Grid>

                <Grid item xs={8}>
                  <TextField
                    fullWidth
                    required
                    label="Cidade"
                    name="cidade"
                    value={formData.cidade}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                </Grid>

                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Estado"
                    name="estado"
                    value={formData.estado}
                    onChange={handleInputChange}
                    disabled={loading}
                    placeholder="UF"
                    inputProps={{ maxLength: 2 }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Botões de Ação */}
          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleVoltar}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              fullWidth
              variant="contained"
              type="submit"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
            >
              {loading ? 'Salvando...' : 'Salvar Cliente'}
            </Button>
          </Box>
        </form>
      </Box>

      {/* Diálogo de Erro */}
      <Dialog open={errorDialogOpen} onClose={handleFecharErro} maxWidth="sm" fullWidth>
        <DialogTitle>Erro ao Cadastrar Cliente</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            Erro ao cadastrar o cliente
          </Alert>
          <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
            <Typography variant="body2">
              {errorMessage}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFecharErro} variant="contained" color="error">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de Sucesso */}
      <Dialog open={successDialogOpen} maxWidth="sm" fullWidth>
        <DialogTitle>Cliente Cadastrado com Sucesso!</DialogTitle>
        <DialogContent>
          <Alert severity="success" sx={{ mb: 2 }}>
            {responseData?.mensagem || 'Cliente cadastrado com sucesso!'}
          </Alert>
          {responseData && (
            <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>ID:</strong> {responseData.id}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Razão Social:</strong> {responseData.razao}
              </Typography>
              <Typography variant="body2">
                <strong>CNPJ:</strong> {responseData.cnpj}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFecharSucesso} variant="contained">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CadCliente;
